<script setup>
import {computed, ref, watch} from 'vue'
import ContentTypeLabel from '@/common/ContentTypeLabel.vue'
import ContentUsageCount from '@/common/ContentUsageCount.vue'
import ItemStatusIcons from "@/common/ItemStatusIcons.vue"
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useTemplatesStore} from '@/stores/templates'
import {useItemDataStore} from '@/stores/itemData'
import {useSiteTreeStore} from '@/stores/siteTree'

const props = defineProps({
    pageContentId: Number,
    siteTreeId: String
})

// --- Pinia Stores ---

const pageContentDataStore = usePageContentDataStore()
const templatesStore = useTemplatesStore()
const itemDataStore = useItemDataStore()

const siteTreeStore = useSiteTreeStore(props.siteTreeId)

// --- Computed ---

const selectContentMode = computed(() => {
    return siteTreeStore.selectContentMode
})

const showContentTypeButtons = computed(() => {
    return siteTreeStore.showContentTypeButtons
})

const selectedContentId = computed(() => {
    return siteTreeStore.selectedContentId
})

const lastSelectedContentId = computed(() => {
    return siteTreeStore.lastSelectedContentId
})

const contentId = computed(() => {
    return pageContentDataStore.items?.find(o => o.id === props.pageContentId)?.contentId
})

const isSelected = computed(() => {
    return selectedContentId.value === props.pageContentId
})

const isLastSelected = computed(() => {
    return (
        selectedContentId.value === 0 &&
        lastSelectedContentId.value === props.pageContentId
    )
})

// Reactive ref for asynchronously fetched content
const content = ref(null)

// Watch the computed contentId property to handle fetching item data
watch(contentId, async (newContentId) => {
    if (newContentId !== undefined && newContentId !== null) {
        content.value = await itemDataStore.get({
            tableName: 'content',
            id: newContentId
        })
    } else {
        content.value = null
    }
}, {immediate: true})

const contentTypeTitle = computed(() => {
    if (content.value) {
        return templatesStore.contentTypes?.find(o => o.id === content.value.contentType)?.title
    }
    return undefined
})

// --- Methods ---

const selectContent = () => {
    if (siteTreeStore.onContentSelect) {
        siteTreeStore.onContentSelect(props.pageContentId)
    }
}
</script>

<template>
    <a v-if="content && !content.isArchived" href="#" @click.prevent="selectContent"
       :class="{ 'active': isSelected, 'selected-secondary': isLastSelected }" :data-content-id="pageContentId"
       class="list-group-item list-group-item-action d-flex align-items-center py-1">

        <div class="flex-fill">
            <span v-if="content.title">{{ content.title }}</span>

            <ContentTypeLabel v-if="showContentTypeButtons && content" :content-type-id="content.contentType"
                              class="ms-1"/>
            <span v-else :class="{'ms-2 text-body-secondary': content.title}">{{ contentTypeTitle }}</span>
        </div>

        <ContentUsageCount v-if="!selectContentMode && content" class="ms-1 small" :page-content-id="pageContentId"/>

        <ItemStatusIcons :table-name="'content'" :id="contentId"/>
    </a>
</template>